import { useMutation } from '@tanstack/react-query';
import { Award, ClipboardCheck, Megaphone, ShieldCheck, Sparkles, UtensilsCrossed } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { CampusScene } from '@/components/auth/CampusScene';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/use-auth';
import { getValidationMessage } from '@/lib/validation';

const HERO_FEATURES = [
  { icon: ClipboardCheck, key: 'auth.hero_feature_operations' },
  { icon: UtensilsCrossed, key: 'auth.hero_feature_cafeteria' },
  { icon: Award, key: 'auth.hero_feature_rewards' },
  { icon: Megaphone, key: 'auth.hero_feature_community' },
] as const;

let isGoogleIdentityInitialized = false;

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
const HOSTED_DOMAIN = import.meta.env.VITE_GOOGLE_OAUTH_HOSTED_DOMAIN;

if (!GOOGLE_CLIENT_ID) {
  throw new Error('VITE_GOOGLE_OAUTH_CLIENT_ID is required');
}
if (!HOSTED_DOMAIN) {
  throw new Error('VITE_GOOGLE_OAUTH_HOSTED_DOMAIN is required');
}

export function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [googleError, setGoogleError] = useState('');
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const googleCallbackRef = useRef<(response: { credential?: string }) => void>(() => {});

  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';

  const loginSchema = yup.object({
    email: yup.string().trim().email(t('auth.error_invalid_email')).required(t('auth.error_email_required')),
    password: yup.string().required(t('auth.error_password_required')),
  });

  const passwordMutation = useMutation({
    mutationFn: async (creds: { email: string; password: string }) => {
      await login(creds.email, creds.password);
    },
    onSuccess: () => navigate(from, { replace: true }),
  });

  const { mutate: mutateGoogleLogin } = useMutation({
    mutationFn: async (idToken: string) => {
      await loginWithGoogle(idToken);
    },
    onSuccess: () => navigate(from, { replace: true }),
    // Prefer the thrown error's message (e.g. "not staff") over the generic fallback, the
    // same way the password form below reads passwordMutation.error.message.
    onError: error => setGoogleError(error instanceof Error ? error.message : t('auth.error_google_sign_in')),
  });

  // eslint-disable-next-line react-hooks/refs
  googleCallbackRef.current = response => {
    if (response.credential) mutateGoogleLogin(response.credential);
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    let cancelled = false;

    const renderGoogleButton = () => {
      if (cancelled || !window.google || !googleButtonRef.current) return false;

      if (!isGoogleIdentityInitialized) {
        // Multiple Workspace domains are allowed (e.g. risphuket.ac.th + brain-ed.co.th),
        // so we don't pass a single-domain `hd` hint — it would hide accounts from the
        // other domain. The authoritative check is server-side in POST /auth/google.
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: response => googleCallbackRef.current(response),
        });
        isGoogleIdentityInitialized = true;
      }

      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        width: 320,
      });
      return true;
    };

    // The GIS client script is loaded `async defer`, so `window.google` may not
    // be ready when this effect first runs. Previously the effect returned early
    // with no retry, so on a slow/cold script load the button never rendered at
    // all. Poll until the script is ready so the Google button always appears;
    // surface an error if it never loads so the option isn't silently missing.
    if (renderGoogleButton()) return;

    let attempts = 0;
    const maxAttempts = 100; // ~10s at 100ms
    const interval = setInterval(() => {
      attempts += 1;
      if (renderGoogleButton() || attempts >= maxAttempts) {
        clearInterval(interval);
        if (attempts >= maxAttempts && !window.google) {
          setGoogleError(t('auth.error_google_unavailable'));
        }
      }
    }, 100);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [mutateGoogleLogin, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    try {
      const validated = await loginSchema.validate({ email, password }, { abortEarly: false });
      passwordMutation.mutate(validated);
    } catch (error) {
      setFormError(getValidationMessage(error, t('auth.error_fill_required')));
    }
  };

  return (
    <div className="min-h-screen bg-muted/40 lg:grid lg:grid-cols-2">
      <div className="flex items-center justify-center bg-background px-6 py-10 sm:px-8 lg:px-12">
        <div className="w-full max-w-md space-y-6">
          <div className="space-y-2">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-md">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('auth.welcome_back')}</h1>
            <p className="text-sm text-muted-foreground">{t('auth.subtitle')}</p>
          </div>

          <Card className="border-border shadow-xl">
            <CardHeader className="space-y-1">
              <CardTitle className="text-xl">{t('auth.sign_in')}</CardTitle>
              <CardDescription>{t('auth.card_description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div ref={googleButtonRef} className="flex justify-center" />
              {googleError && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  {googleError}
                </div>
              )}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{t('auth.or')}</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {formError && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {formError}
                  </div>
                )}
                {passwordMutation.error && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {passwordMutation.error instanceof Error
                      ? passwordMutation.error.message
                      : t('auth.error_login_failed')}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.email_placeholder')}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder={t('auth.password_placeholder')}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={passwordMutation.isPending}>
                  {passwordMutation.isPending ? t('auth.signing_in') : t('auth.sign_in')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="relative hidden overflow-hidden bg-slate-900 p-10 text-white lg:flex">
        <div className="absolute -right-28 -top-20 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
        <div className="absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.09) 1px, transparent 0)',
            backgroundSize: '22px 22px',
            maskImage: 'radial-gradient(ellipse at 65% 35%, black, transparent 72%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 65% 35%, black, transparent 72%)',
          }}
        />

        <div className="relative z-10 flex h-full w-full flex-col rounded-3xl border border-white/10 bg-white/4 p-10 backdrop-blur">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs font-medium text-white/90">
            <Sparkles className="h-3.5 w-3.5" />
            {t('auth.risp_administration')}
          </div>

          <div className="flex flex-1 items-center justify-center py-6">
            <CampusScene className="w-full max-w-md" />
          </div>

          <div className="space-y-6">
            <h2 className="max-w-xl text-4xl font-semibold leading-tight">{t('auth.hero_heading')}</h2>
            <div className="grid gap-3 text-sm text-white/85">
              {HERO_FEATURES.map(({ icon: Icon, key }) => (
                <div key={key} className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-white/70" />
                  {t(key)}
                </div>
              ))}
            </div>
          </div>

          <p className="mt-8 text-xs text-white/60">{t('auth.authorized_staff_only')}</p>
        </div>
      </div>
    </div>
  );
}
